<app-banner
  [title]="'Detail de l evenement de sante'"
  [links]="links">
</app-banner>

<div class="container !pb-10">
  <div class="mb-4">
    <button
      type="button"
      (click)="goBack()"
      class="inline-flex items-center rounded-md border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-50">
      Retour au suivi sante
    </button>
  </div>

  <div *ngIf="isLoading" class="rounded-xl bg-white p-8 shadow-sm">
    <div class="flex items-center justify-center">
      <div class="text-center">
        <div class="mx-auto mb-3 h-8 w-8 animate-spin rounded-full border-b-2 border-primary"></div>
        <p class="text-sm text-gray-500">Chargement des details...</p>
      </div>
    </div>
  </div>

  <div *ngIf="error && !isLoading" class="rounded-xl border border-red-200 bg-red-50 p-6">
    <h3 class="mb-2 text-sm font-semibold text-red-800">Erreur de chargement</h3>
    <p class="text-sm text-red-700">{{ error }}</p>
  </div>

  <div *ngIf="event && !isLoading" class="space-y-6">
    <section class="rounded-2xl bg-white p-6 shadow-sm">
      <div class="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <div class="mb-3 flex flex-wrap items-center gap-2">
            <span class="rounded-full bg-primary/10 px-3 py-1 text-xs font-semibold text-primary">
              {{ getEventTypeLabel(event.type) }}
            </span>
            <span
              class="rounded-full px-3 py-1 text-xs font-semibold"
              [class]="getStatusBadgeClass(event.status)">
              {{ getStatusText(event.status) }}
            </span>
          </div>
          <h2 class="mb-2 !text-[22px] font-semibold text-gray-900 lg:!text-[24px]">
            {{ event.title }}
          </h2>
          <p class="max-w-3xl text-sm text-gray-600">
            {{ event.description || 'Aucune description disponible.' }}
          </p>
        </div>
      </div>
    </section>

    <section class="rounded-2xl bg-white p-5 shadow-sm">
      <div class="mb-5 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h3 class="!text-[16px] font-semibold text-gray-900">Documents lies</h3>
          <p class="mt-1 text-sm text-gray-500">
            Rattache des documents deja presents ou charge un nouveau document avant rattachement.
          </p>
        </div>
        <div class="flex flex-wrap gap-2">
          <button
            type="button"
            (click)="openSelectExistingDocumentsModal()"
            class="rounded-md border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-50">
            Choisir un document existant
          </button>
          <button
            type="button"
            (click)="openUploadDocumentModal()"
            class="rounded-md border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-50">
            Uploader un document
          </button>
        </div>
      </div>

      <div class="rounded-xl border border-gray-100 bg-gray-50 p-4">
        <div *ngIf="attachedDocuments.length === 0" class="text-sm text-gray-500">
          Aucun document rattache pour le moment.
        </div>

        <div *ngIf="attachedDocuments.length > 0" class="grid gap-3 lg:grid-cols-2">
          <div
            *ngFor="let doc of attachedDocuments"
            class="flex items-center justify-between rounded-lg border border-gray-200 bg-white px-4 py-3">
            <div class="min-w-0">
              <p class="truncate text-sm font-medium text-gray-900">
                {{ doc.filename || 'Document sans nom' }}
              </p>
              <p class="mt-1 text-xs text-gray-500">
                {{ doc.documentType?.label || 'Type non renseigne' }}
              </p>
            </div>
            <button
              type="button"
              (click)="onViewDocument(doc)"
              class="ml-3 rounded-md border border-gray-200 px-3 py-2 text-xs font-medium text-gray-700 hover:bg-gray-50">
              Voir
            </button>
          </div>
        </div>
      </div>
    </section>

    <section class="grid gap-4 lg:grid-cols-2">
      <article class="rounded-2xl bg-white p-5 shadow-sm">
        <div class="mb-4 flex items-center gap-2">
          <lucide-icon [img]="CalendarClockIcon" [size]="18"></lucide-icon>
          <h3 class="!text-[16px] font-semibold text-gray-900">Programmation</h3>
        </div>
        <dl class="space-y-4">
          <div>
            <dt class="text-xs uppercase tracking-wide text-gray-400">Date</dt>
            <dd class="mt-1 text-sm text-gray-800">{{ formatDate(event.date) }}</dd>
          </div>
          <div>
            <dt class="text-xs uppercase tracking-wide text-gray-400">Heure</dt>
            <dd class="mt-1 text-sm text-gray-800">{{ formatTimeRange(event) }}</dd>
          </div>
          <div>
            <dt class="text-xs uppercase tracking-wide text-gray-400">Lieu</dt>
            <dd class="mt-1 text-sm text-gray-800">{{ event.location || event.structureName || 'Non renseigne' }}</dd>
          </div>
        </dl>
      </article>

      <article class="rounded-2xl bg-white p-5 shadow-sm">
        <div class="mb-4 flex items-center gap-2">
          <lucide-icon [img]="UserRoundIcon" [size]="18"></lucide-icon>
          <h3 class="!text-[16px] font-semibold text-gray-900">Intervenants</h3>
        </div>
        <dl class="space-y-4">
          <div>
            <dt class="text-xs uppercase tracking-wide text-gray-400">Professionnel</dt>
            <dd class="mt-1 text-sm text-gray-800">{{ event.doctor || event.professionalName || 'Non renseigne' }}</dd>
          </div>
          <div>
            <dt class="text-xs uppercase tracking-wide text-gray-400">Structure</dt>
            <dd class="mt-1 text-sm text-gray-800">{{ event.structureName || event.location || 'Non renseigne' }}</dd>
          </div>
          <div>
            <dt class="text-xs uppercase tracking-wide text-gray-400">Beneficiaire</dt>
            <dd class="mt-1 text-sm text-gray-800">
              {{ event.user?.firstName || event.user?.lastName ? (event.user?.firstName + ' ' + event.user?.lastName) : 'Utilisateur principal' }}
            </dd>
          </div>
        </dl>
      </article>
    </section>

    <section class="grid gap-4 lg:grid-cols-2">
      <article class="rounded-2xl bg-white p-5 shadow-sm">
        <div class="mb-4 flex items-center gap-2">
          <lucide-icon [img]="FileTextIcon" [size]="18"></lucide-icon>
          <h3 class="!text-[16px] font-semibold text-gray-900">Informations medicales</h3>
        </div>
        <dl class="space-y-4">
          <div>
            <dt class="text-xs uppercase tracking-wide text-gray-400">Motif</dt>
            <dd class="mt-1 text-sm text-gray-800">{{ event.appointment?.reason?.label || 'Non renseigne' }}</dd>
          </div>
          <div>
            <dt class="text-xs uppercase tracking-wide text-gray-400">Numero de passeport</dt>
            <dd class="mt-1 text-sm text-gray-800">{{ event.passportNumber || event.user?.passportNumber || 'Non renseigne' }}</dd>
          </div>
          <div>
            <dt class="text-xs uppercase tracking-wide text-gray-400">Type</dt>
            <dd class="mt-1 text-sm text-gray-800">{{ getEventTypeLabel(event.type) }}</dd>
          </div>
        </dl>
      </article>

      <article class="rounded-2xl bg-white p-5 shadow-sm">
        <div class="mb-4 flex items-center gap-2">
          <lucide-icon [img]="NotebookPenIcon" [size]="18"></lucide-icon>
          <h3 class="!text-[16px] font-semibold text-gray-900">Notes et suivi</h3>
        </div>
        <dl class="space-y-4">
          <div>
            <dt class="text-xs uppercase tracking-wide text-gray-400">Notes</dt>
            <dd class="mt-1 whitespace-pre-line text-sm text-gray-800">
              {{ event.notes || 'Aucune note disponible.' }}
            </dd>
          </div>
          <div *ngIf="event.amount != null">
            <dt class="text-xs uppercase tracking-wide text-gray-400">Montant</dt>
            <dd class="mt-1 text-sm font-medium text-green-700">
              {{ formatAmount(event.amount, event.currency) }}
            </dd>
          </div>
        </dl>
      </article>
    </section>

    <section class="grid gap-4 lg:grid-cols-3">
      <article class="rounded-2xl bg-white p-5 shadow-sm">
        <div class="mb-3 flex items-center gap-2">
          <lucide-icon [img]="HospitalIcon" [size]="18"></lucide-icon>
          <h3 class="!text-[16px] font-semibold text-gray-900">References</h3>
        </div>
        <p class="text-xs text-gray-400">Identifiant evenement</p>
        <p class="mt-1 break-all text-sm text-gray-800">{{ event.id }}</p>
      </article>

      <article class="rounded-2xl bg-white p-5 shadow-sm">
        <div class="mb-3 flex items-center gap-2">
          <lucide-icon [img]="ShieldCheckIcon" [size]="18"></lucide-icon>
          <h3 class="!text-[16px] font-semibold text-gray-900">Creation</h3>
        </div>
        <p class="text-xs text-gray-400">Cree le</p>
        <p class="mt-1 text-sm text-gray-800">{{ formatDate(event.createdAt) }}</p>
      </article>

      <article class="rounded-2xl bg-white p-5 shadow-sm">
        <div class="mb-3 flex items-center gap-2">
          <lucide-icon [img]="CircleDollarSignIcon" [size]="18"></lucide-icon>
          <h3 class="!text-[16px] font-semibold text-gray-900">Mise a jour</h3>
        </div>
        <p class="text-xs text-gray-400">Derniere mise a jour</p>
        <p class="mt-1 text-sm text-gray-800">{{ formatDate(event.updatedAt) }}</p>
      </article>
    </section>

  </div>
</div>
